// Конструктор главной: вкладки и их блоки (см. backend/app/services/page_blocks.py).
//
// Рамка главной — секции раздела `main` (заголовок, виджет, ряд моделей, FAQ). Среди них стоит
// секция-контейнер `tabs`: её место в потоке задаёт, где рисуются строка вкладок и содержимое.
// Блок вкладки — любая секция с разделом `tab:<слаг>`; вся выдача приезжает одним ответом
// (`?page=main&tabs=1`), поэтому переключение вкладок запросов не делает.
import type { PublicTarget } from '../../utils/linkTarget'

export const TAB_LANE_PREFIX = 'tab:'
/** Параметр адреса с активной вкладкой: `/?tab=business`. У вкладки по умолчанию параметра нет. */
export const TAB_PARAM = 'tab'

export interface HomeSection {
	id: string
	section_type: string
	sort_order: number
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	config: Record<string, any>
	target_pages?: string[]
	/** false приезжает только в стафф-превью — сайт помечает блок «видите только вы». */
	is_visible?: boolean
}

export interface HomeTab {
	slug: string
	label: string
	icon?: string
	is_visible?: boolean
	/** Вкладка «только сотрудникам»: клиенту сервер её не отдаёт, сотрудник видит с янтарной точкой. */
	staff_only?: boolean
}

/** Блок или контейнер вкладок в режиме «только сотрудникам» (config.staff_only): приезжает только сотруднику. */
export const isStaffOnly = (s: Pick<HomeSection, 'config'>): boolean => s.config?.staff_only === true

/** Текст на кадре (владелец 26.09 выбрал три приёма): «Полоса» — тёмная полоса под белым текстом, «Плашка» —
 *  матовая плашка цвета темы, «Тон по кадру» — по яркости кадра в зоне текста (её меряет сервер при сохранении
 *  блока): светлая зона — белая вуаль и тёмный текст, тёмная — полоса. У блока — умолчание, у карточки — своё. */
export type ShowcaseTextStyle = 'band' | 'plate' | 'tone'
export const SHOWCASE_TEXT_STYLES: readonly ShowcaseTextStyle[] = ['band', 'plate', 'tone']
export const DEFAULT_TEXT_STYLE: ShowcaseTextStyle = 'tone'

/** Тон кадра в зонах текста: top — заголовок «акцента» и бренда, bottom — подпись и строки, all — узкая плитка. */
export interface ShowcaseTone {
	src?: string
	top?: 'light' | 'dark'
	bottom?: 'light' | 'dark'
	all?: 'light' | 'dark'
}

const isTextStyle = (v: unknown): v is ShowcaseTextStyle => SHOWCASE_TEXT_STYLES.includes(v as ShowcaseTextStyle)

/** Режим плитки: свой у карточки, иначе блока, иначе «Тон по кадру». */
export function showcaseTextStyle(own: unknown, block: unknown): ShowcaseTextStyle {
	return isTextStyle(own) ? own : isTextStyle(block) ? block : DEFAULT_TEXT_STYLE
}

/** Классы плитки с кадром: режим (tm-*) и светлые зоны кадра в «Тоне по кадру» (tz-*-light). Тон не измерен —
 *  светлых зон нет, и плитка выглядит «Полосой»: белый текст на тёмной полосе читается на любом кадре. */
export function mediaTextClasses(style: ShowcaseTextStyle, tone?: ShowcaseTone | null): string {
	let cls = ` tm-${style}`
	if (style === 'tone' && tone) {
		for (const zone of ['top', 'bottom', 'all'] as const) if (tone[zone] === 'light') cls += ` tz-${zone}-light`
	}
	return cls
}

export interface ShowcaseMedia {
	image_url?: string
	image_preview_url?: string | null
	video_url?: string
	video_preview_url?: string | null
	video_loop_url?: string | null
	w?: number
	h?: number
	tone?: ShowcaseTone
}

export interface ShowcaseLine {
	title: string
	text?: string
	target?: PublicTarget
}

export type ShowcaseCardStyle = 'brand' | 'feature' | 'spotlight'

export interface ShowcaseCardItem {
	id: string
	type: 'card'
	style: ShowcaseCardStyle
	cols: 1 | 2
	/** Высота в рядах; 3 — вертикальная плитка под вертикальный контент (тренды). */
	rows: 1 | 2 | 3
	/** «Узкая» — половина ряда по высоте: в плитке остаются только иконка и название. */
	short?: boolean
	title: string
	text?: string
	badge?: string
	icon?: string
	tint?: string
	cta_label?: string
	media?: ShowcaseMedia
	target?: PublicTarget
	lines?: ShowcaseLine[]
	is_visible?: boolean
	text_style?: ShowcaseTextStyle
}

/** Пресет общего пула: сервер подставил карточку и адрес (лендинг либо генератор). */
export interface ShowcasePresetItem {
	id: string
	type: 'preset'
	cols: 1 | 2
	rows: 1 | 2 | 3
	short?: boolean
	card_id: string
	card: {
		id: string
		title?: string
		description?: string
		background_url?: string
		background_preview_url?: string | null
		video_url?: string
		video_preview_url?: string | null
		video_loop_url?: string | null
		badge_text?: string
	}
	target?: PublicTarget
	is_visible?: boolean
	text_style?: ShowcaseTextStyle
	/** Тон кадра карточки пула — сервер отдаёт его, только если мерил нынешний кадр. */
	tone?: ShowcaseTone
}

export type ShowcaseItem = ShowcaseCardItem | ShowcasePresetItem

export interface ShowcaseLead {
	style: 'intro' | 'spotlight'
	title: string
	title_muted?: string
	text?: string
	badge?: string
	media?: ShowcaseMedia
	cta?: { label: string; target: PublicTarget }
}

export interface ShowcaseConfig {
	title?: string
	subtitle?: string
	columns?: number
	/** Ширина блока на странице: по центру (по умолчанию) либо во всю ширину контентной части. */
	width?: 'wide' | string
	/** Чем плитка отвечает на наведение — один стиль на весь блок. */
	hover?: string
	/** Текст на кадре по умолчанию для плиток блока. */
	text_style?: ShowcaseTextStyle
	lead?: ShowcaseLead
	items?: ShowcaseItem[]
}

export const sectionLanes = (s: Pick<HomeSection, 'target_pages'>): string[] =>
	s.target_pages?.length ? s.target_pages : ['main']

/** Секция рамки: рисуется в общем потоке страницы. Блок, живущий только во вкладках, сюда не входит. */
export const isFrameSection = (s: Pick<HomeSection, 'target_pages'>): boolean => sectionLanes(s).includes('main')

export function tabsOf(section: Pick<HomeSection, 'config'>): HomeTab[] {
	const raw = section.config?.tabs
	if (!Array.isArray(raw)) return []
	return raw.filter((t): t is HomeTab => !!t && typeof t.slug === 'string' && typeof t.label === 'string')
}

/** Блоки вкладки в порядке страницы (общий sort_order секций). */
export function tabBlocks<T extends Pick<HomeSection, 'target_pages' | 'sort_order'>>(sections: T[], slug: string): T[] {
	const lane = TAB_LANE_PREFIX + slug
	return sections.filter((s) => sectionLanes(s).includes(lane)).sort((a, b) => a.sort_order - b.sort_order)
}

/** Активная вкладка: запрошенная в адресе, если она есть среди вкладок, иначе первая. */
export function pickActiveTab(tabs: HomeTab[], requested: string | null): string | null {
	if (!tabs.length) return null
	if (requested && tabs.some((t) => t.slug === requested)) return requested
	return tabs[0].slug
}

/** Адрес вкладки: у первой — чистый адрес страницы (канонический), у остальных `?tab=<слаг>`.
 *  Чужие параметры (utm и т.п.) сохраняются — их читает сборщик UTM в App.tsx. */
export function tabSearch(current: string, tabs: HomeTab[], slug: string): string {
	const params = new URLSearchParams(current)
	if (tabs[0]?.slug === slug) params.delete(TAB_PARAM)
	else params.set(TAB_PARAM, slug)
	const out = params.toString()
	return out ? `?${out}` : ''
}

/** Сетка «Витрины»: число колонок из настройки блока, 2–5 (по умолчанию 4). */
export function showcaseColumns(cfg: Pick<ShowcaseConfig, 'columns'>): number {
	const n = Math.round(Number(cfg.columns) || 4)
	return Math.min(5, Math.max(2, n))
}

/** Ответ плитки на наведение — настройка блока, одна на всю сетку (см. _home-showcase.scss). */
export const SHOWCASE_HOVERS = ['arrow', 'reveal', 'zoom', 'glow', 'none'] as const
export type ShowcaseHover = (typeof SHOWCASE_HOVERS)[number]

export const showcaseHover = (cfg: Pick<ShowcaseConfig, 'hover'>): ShowcaseHover =>
	(SHOWCASE_HOVERS as readonly string[]).includes(String(cfg.hover)) ? (cfg.hover as ShowcaseHover) : 'arrow'

/** Блок во всю ширину контентной части: витрина или лента, которым разрешено выйти из колонки страницы
 *  (1200px). У ленты размер плиток прежний — в ряд встаёт больше плиток (TrendFeedSection, fullWidth). */
export const isWideBlock = (s: Pick<HomeSection, 'section_type' | 'config'>): boolean =>
	(s.section_type === 'showcase' || s.section_type === 'trend_feed') && s.config?.width === 'wide'

/** Какую картинку грузить карточке: крупным (2 колонки, акцентным) — оригинал, остальным превью
 *  (≤512px) с откатом на оригинал. Карточки списков оригиналы не грузят — правило проекта. */
export function showcaseImage(media: ShowcaseMedia | undefined, large: boolean): { src: string; fallback?: string } | null {
	if (!media?.image_url) return null
	if (large || !media.image_preview_url) return { src: media.image_url }
	return { src: media.image_preview_url, fallback: media.image_url }
}

/** Порядок вывода сетки витрины. Узкая карточка всегда стоит в своей ячейке — обычной клетке сетки: две узкие
 *  одной ширины, идущие подряд, делят её друг под другом, одиночная занимает верх, низ остаётся пустым. Так узкие
 *  встают ровно туда, куда их поставили, а сетка не съезжает на полряда. Скрытая карточка (видна только в
 *  стафф-превью) в пару не встаёт и пару не разрывает — у клиента её нет. Зеркало — admin showcaseModel.ts.
 *  Возвращает индексы элементов по группам; slot — группа узких. */
export function showcaseSlots(items: readonly { short?: boolean; cols: number; is_visible?: boolean }[]): { slot: boolean; at: number[] }[] {
	const out: { slot: boolean; at: number[] }[] = []
	const taken = new Set<number>()
	items.forEach((item, i) => {
		if (taken.has(i)) return
		if (!item.short) {
			out.push({ slot: false, at: [i] })
			return
		}
		const at = [i]
		if (item.is_visible !== false) {
			let j = i + 1
			while (j < items.length && items[j].is_visible === false) j++
			const next = items[j]
			if (next?.short && next.cols === item.cols) {
				at.push(j)
				taken.add(j)
			}
		}
		out.push({ slot: true, at })
	})
	return out
}
